import {useSearchParams} from 'react-router-dom'
import {RestrictionRuleRequestType, RestrictionRuleRequestCreateRequestFields} from '@/types/restriction-rule-request'

// WHY: PARAM_MODE is a URL search-param name, not a record field — the backend
// has no `mode` on RestrictionRuleRequestCreateRequest. Wizard routing only.
const PARAM_MODE = 'mode'
const PARAM_RULE_ID = RestrictionRuleRequestCreateRequestFields.ruleId
const PARAM_REQUEST_TYPE = RestrictionRuleRequestCreateRequestFields.requestType
const MODE_UPDATE = 'update'

export interface RestrictionRuleWizardModeState {
    /** True when the URL search params carry mode=update and a ruleId — the wizard should preload from an existing rule. */
    isUpdateFromUrl: boolean
    /** Resolved rule id from URL — set whenever the URL carries one, regardless of {@link isUpdateFromUrl}. The defensive redirect needs it for ACTIVATE/DEACTIVATE flows that arrive without the update mode. */
    ruleId: string | null
    /** Defensive redirect signal — the wizard never renders ACTIVATE / DEACTIVATE flows. */
    requestTypeParam: RestrictionRuleRequestType | null
}

function parseRequestType(value: string | null): RestrictionRuleRequestType | null {
    if (value === RestrictionRuleRequestType.CREATE) return RestrictionRuleRequestType.CREATE
    if (value === RestrictionRuleRequestType.UPDATE) return RestrictionRuleRequestType.UPDATE
    if (value === RestrictionRuleRequestType.ACTIVATE) return RestrictionRuleRequestType.ACTIVATE
    if (value === RestrictionRuleRequestType.DEACTIVATE) return RestrictionRuleRequestType.DEACTIVATE
    return null
}

/**
 * URL-driven dispatch for the RestrictionRuleRequestCreateWizard.
 *
 * Mirrors the shape of {@link useAutoAssignRuleRequestWizardMode} from the
 * auto-assign wizard: the page never reads {@code searchParams} directly —
 * this hook centralises the parsing rules so the URL surface area is testable
 * in isolation.
 *
 * Supported search params:
 *  - mode=update + ruleId=&lt;UUID&gt; — preload from an existing rule.
 *  - requestType=ACTIVATE | DEACTIVATE — defensive redirect signal (these never enter the wizard).
 */
export function useRestrictionRuleRequestWizardMode(): RestrictionRuleWizardModeState {
    const [searchParams] = useSearchParams()
    const mode = searchParams.get(PARAM_MODE)
    const ruleId = searchParams.get(PARAM_RULE_ID)
    const isUpdateFromUrl = mode === MODE_UPDATE && !!ruleId

    return {
        isUpdateFromUrl,
        ruleId: ruleId ?? null,
        requestTypeParam: parseRequestType(searchParams.get(PARAM_REQUEST_TYPE)),
    }
}
